import { useGetSessionToken } from '@homestake/shared/hooks';
import { batch } from '@legendapp/state';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { BetTowersCommand } from '../api';
import { postApiGameBetMutation } from '../api/__generated__/@tanstack/react-query.gen';
import {
  cashoutAnimationState$,
  manualBetState$,
  ratAnimationState$,
  towersState$,
  winDisplayState$,
} from '../state';
import { freeBetState$ } from '../state/free-bet.state';
import { sessionState$ } from '../state/session.state';
import { walletState$ } from '../state/wallet.state';
import { showErrorNotification } from '../utils';
import { countRowsCleared, findLastPickedResult } from '../utils/towers.utils';
import { useBackendErrorNotification } from './useBackendErrorNotification.hook';
import { useCashoutSound } from './useCashoutSound.hook';
import { useLossSound } from './useLossSound.hook';
import { useWinSound } from './useWinSound.hook';

export const useManualBet = () => {
  const { t } = useTranslation();
  const { token } = useGetSessionToken();
  const { showBackendErrorNotification } = useBackendErrorNotification();

  const { playSound: playCashoutSound } = useCashoutSound();
  const { playSound: playLossSound } = useLossSound();
  const { playSound: playWinSound } = useWinSound();

  const mutation = useMutation({
    ...postApiGameBetMutation(),
    onSuccess: (
      { balance, isEnd, towersResults, win, baseBonusResponseDto },
      { body }
    ) => {
      const { pickedBrick } = body as BetTowersCommand;

      batch(() => {
        freeBetState$.update(baseBonusResponseDto, isEnd === true);

        if (balance !== undefined) {
          walletState$.setBalance(balance);
        }

        if (isEnd && towersResults?.length) {
          manualBetState$.setIsBetting(false);
          towersState$.setAllBricks(towersResults ?? []);
        }

        if (!isEnd) {
          towersState$.pickBrick({
            isBrick: true,
            isMine: false,
            column: pickedBrick,
          });
        }
      });

      const bricks = towersState$.bricks.peek();

      // Find the last picked position and determine win/loss
      const { isWin } = findLastPickedResult(bricks);

      // Play appropriate sound
      if (isWin === true && isEnd) {
        ratAnimationState$.triggerAnimation('win');
        cashoutAnimationState$.triggerAnimation('cashout', 3000);
        playCashoutSound();

        // Show win display for manual bet win
        const rowsCleared = countRowsCleared(bricks);
        const betAmount = manualBetState$.betAmount.peek();

        winDisplayState$.showWinDisplay({
          betAmount,
          winAmount: win ?? 0,
          rowsCleared,
        });
      }

      if (isWin === true && !isEnd) {
        playWinSound();
      }

      if (isWin === false) {
        ratAnimationState$.triggerAnimation('bad');
        playLossSound();
      }
    },
    onError: (error) => {
      showBackendErrorNotification(error);
    },
  });

  const handleManualBet = useCallback(
    (pickedBrickIndex: number) => {
      if (!token) {
        showErrorNotification({
          title: t('notifications.errorTitle'),
          message: t('notifications.noTokenFoundMessage'),
          dataTestId: 'manual-bet-no-token',
        });
        return;
      }
      mutation.mutate({
        body: {
          gameRoundId: sessionState$.gameRoundId.peek(),
          token,
          pickedBrick: pickedBrickIndex,
        },
      });
    },
    [mutation, token, t]
  );

  return { mutation, handleManualBet, findLastPickedResult };
};
